import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import LegalNotice from "@/pages/LegalNotice";
import Home from "@/pages/Home";
import Reply from "@/pages/Reply";
import Analyze from "@/pages/Analyze";
import Create from "@/pages/Create";
import MyWork from "@/pages/MyWork";
import KidsMap from "@/pages/KidsMap";
import Debate from "@/pages/Debate";
import Library from "@/pages/Library";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import Partners from "@/pages/Partners";
import PartnerDetail from "@/pages/PartnerDetail";
import KnowledgeDetail from "@/pages/KnowledgeDetail";
import YouthSpace from "@/pages/YouthSpace";
import Letters from "@/pages/Letters";
import About from "@/pages/About";
import Community from "@/pages/Community";
import Coach from "@/pages/Coach";
import History from "@/pages/History";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Premium from "@/pages/Premium";
import Admin from "@/pages/Admin";
import Rights from "@/pages/Rights";
import RightDetail from "@/pages/RightDetail";
import PublicRight from "@/pages/PublicRight";
import RightsHelp from "@/pages/RightsHelp";
import News from "@/pages/News";
import VoiceFeedback from "@/pages/VoiceFeedback";
import Nouveautes from "@/pages/Nouveautes";
import Ideas from "@/pages/Ideas";
import Rules from "@/pages/Rules";
import QuoteOfDay from "@/pages/QuoteOfDay";
import Authors from "@/pages/Authors";
import AuthorDetail from "@/pages/AuthorDetail";
import AfroMap from "@/pages/AfroMap";
import Situation from "@/pages/Situation";
import EspaceCommunautaire from "@/pages/EspaceCommunautaire";
import MemberProfile from "@/pages/MemberProfile";
import Layout from "@/components/Layout";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#D4AF37] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cgu" element={<Terms />} />
      <Route path="/mentions-legales" element={<LegalNotice />} />
      <Route path="/regles" element={<Rules />} />
      <Route path="/f/:slug" element={<PublicRight />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/citation" element={<QuoteOfDay />} />
        <Route path="/apropos" element={<About />} />
        <Route path="/reply" element={<Reply />} />
        <Route path="/situation" element={<Situation />} />
        <Route path="/analyze" element={<Analyze />} />
        <Route path="/create" element={<Create />} />
        <Route path="/debate" element={<Debate />} />
        <Route path="/coach" element={<Coach />} />
        <Route path="/jeunes" element={<YouthSpace space="jeunes" />} />
        <Route path="/parents" element={<YouthSpace space="parents" />} />
        <Route path="/rights" element={<Rights />} />
        <Route path="/courriers" element={<Letters />} />
        <Route path="/rights/aide" element={<RightsHelp />} />
        <Route path="/rights/:slug" element={<RightDetail />} />
        <Route path="/actualites" element={<News />} />
        <Route path="/library" element={<Library />} />
        <Route path="/library/:id" element={<KnowledgeDetail />} />
        <Route path="/auteurs" element={<Authors />} />
        <Route path="/auteurs/:slug" element={<AuthorDetail />} />
        <Route path="/carte-afro" element={<AfroMap />} />
        <Route path="/espace-communautaire" element={<EspaceCommunautaire />} />
        <Route path="/membre/:userId" element={<MemberProfile />} />
        <Route path="/bibliotheque" element={<Books />} />
        <Route path="/bibliotheque/:id" element={<BookDetail />} />
        <Route path="/partenaires" element={<Partners />} />
        <Route path="/partenaires/:id" element={<PartnerDetail />} />
        <Route path="/community" element={<Community />} />
        <Route path="/idees" element={<Ideas />} />
        <Route path="/nouveautes" element={<Nouveautes />} />
        <Route path="/votre-avis" element={<VoiceFeedback />} />
        <Route path="/history" element={<History />} />
        <Route path="/mes-travaux" element={<MyWork />} />
        <Route path="/carte-kids" element={<KidsMap />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-center" theme="dark" richColors />
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}
